import { Navigate } from "react-router";
import { useQuery } from "@tanstack/react-query";

import { apiJson, apiUrl } from "./apiClient";
import { getCurrentUser } from "./auth/currentUser";
import DelayedSpinner from "../components/atoms/DelayedSpinner";

let featureFlagsQueryClient = null;

async function getFeatureFlags() {
  return apiJson(apiUrl("/featureFlags"));
}

// Shared by the bootstrap fetch and the `useFeatureFlags` hook so the two can
// never drift into separate cache entries with different retention.
//
// `gcTime: Infinity` is load-bearing: `featureFlagIsEnabled` is a synchronous
// `getQueryData` read with no observer behind it, so under the default 30-minute
// gcTime the entry is garbage-collected mid-session and every synchronous reader
// silently degrades to "flag off". With `refetchOnWindowFocus: false`, nothing
// would ever repopulate it.
const FEATURE_FLAGS_QUERY = {
  queryKey: ["featureFlags"],
  queryFn: getFeatureFlags,
  staleTime: 1000 * 60 * 5,
  gcTime: Infinity,
  refetchOnWindowFocus: false,
};

export const isFeatureEligible = (featureData) => {
  if (!featureData) {
    return false;
  }

  const {
    dealerCode: userDealerCode,
    regionCode: userRegionCode,
    spinId: userSpinId,
  } = getCurrentUser();

  // Symmetric OR: a global enable OR any single dealer/region/spin match grants
  // eligibility. Identity values are truthiness-guarded so a missing (null/"")
  // value can never match an empty or blank targeting entry. Empty or missing
  // targeting arrays never match (optional chaining short-circuits to undefined).
  return (
    featureData.enabled === true ||
    (!!userDealerCode && !!featureData.dealerCodes?.includes(userDealerCode)) ||
    (!!userRegionCode && !!featureData.regionCodes?.includes(userRegionCode)) ||
    (!!userSpinId && !!featureData.spinIDs?.includes(userSpinId))
  );
};

// Fetches feature flags into the shared query cache so later synchronous reads
// via `featureFlagIsEnabled` resolve without a hook.
export async function initFeatureFlags(queryClient, userManager) {
  if (!userManager) {
    return;
  }

  featureFlagsQueryClient = queryClient;

  // `fetchQuery` rather than `prefetchQuery`: prefetch swallows rejections, so a
  // 401 during token renewal would leave the cache empty for the entire session
  // with nothing logged and every flag reading as off.
  try {
    await queryClient.fetchQuery(FEATURE_FLAGS_QUERY);
  } catch (error) {
    console.error("Failed to load feature flags during bootstrap:", error);
  }
}

export const featureFlagIsEnabled = (featureName) => {
  if (!featureFlagsQueryClient) {
    return false;
  }

  const featureFlags =
    featureFlagsQueryClient.getQueryData(["featureFlags"]) || [];
  const featureData = featureFlags.find(
    (feature) => feature.featureName === featureName,
  );

  return isFeatureEligible(featureData);
};

// Custom hook to fetch and manage feature flags using React Query.
// This hook provides access to the feature flags and handles caching and re-fetching.
// Pass { enabled: false } to hold the request back without changing callers
// that want it immediately.
export const useFeatureFlags = ({ enabled = true } = {}) => {
  return useQuery({ ...FEATURE_FLAGS_QUERY, enabled });
};

/**
 * FeatureGuardedRoute — reusable gate for rolling out or migrating a route or
 * component behind a feature flag. Renders one of two components based on the
 * caller's eligibility for `featureName`, resolved live from the feature-flags
 * service. Keep this abstraction: it is flag-agnostic and reused across
 * rollouts and route/component migrations.
 *
 * @param {object} props
 * @param {string} props.featureName - Feature-flag key looked up in the flags list.
 * @param {React.ReactNode} props.enabledComponent - Rendered when the user is eligible.
 * @param {React.ReactNode} [props.disabledComponent] - Rendered when NOT eligible;
 *   when omitted, ineligible users are redirected to `/404`.
 *
 * Behavior:
 * - While flags load: shows a centered loading spinner.
 * - Eligible (+ enabledComponent): renders `enabledComponent`.
 * - Not eligible (+ disabledComponent): renders `disabledComponent`.
 * - Not eligible with no `disabledComponent` (or flags error): redirects to `/404`.
 *
 * Eligibility uses `isFeatureEligible`: a global `enabled: true`, OR a match on
 * the user's SPIN / dealer / region against the flag's `spinIDs` / `dealerCodes`
 * / `regionCodes` targeting arrays — so a flag can be rolled out to specific
 * SPINs, dealers, or regions before a global enable.
 *
 * @example
 * <Route
 *   path="/my-page"
 *   element={
 *     <FeatureGuardedRoute
 *       featureName="my-feature"
 *       enabledComponent={<MyNewPage />}
 *       disabledComponent={<MyLegacyPage />}
 *     />
 *   }
 * />
 *
 * When a rollout is complete and the flag is permanently on everywhere, remove
 * this gate: render the enabled component unconditionally and retire the flag
 * record along with the legacy `disabledComponent`.
 */
export const FeatureGuardedRoute = ({
  featureName,
  enabledComponent,
  disabledComponent,
}) => {
  const { data: featureFlags = [], isLoading, isError } = useFeatureFlags();

  if (isLoading) {
    return <DelayedSpinner />;
  }

  const featureData = featureFlags.find((f) => f.featureName === featureName);
  const isEligible =
    !isError && featureData ? isFeatureEligible(featureData) : false;

  if (isEligible && enabledComponent) {
    return enabledComponent;
  }
  if (!isEligible && disabledComponent) {
    return disabledComponent;
  }
  return <Navigate to="/404" replace />;
};
